import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { CheckCircle2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GET_SUPPORT_CATEGORIES } from "@/graphql/queries/support";
import { FILE_SUPPORT_REQUEST } from "@/graphql/mutations/support";

/**
 * The form: a category and a description, straight into the queue.
 *
 * Not a fallback — a route somebody may prefer. It reaches no AI at all
 * (`fileSupportRequest` makes no provider call), so it is the privacy-safe way
 * in, it cannot be slow, and it works when the provider is down. It is also what
 * somebody wants who already knows exactly what is wrong and does not want a
 * conversation about it.
 *
 * The category comes from the server's own closed list, so the form cannot offer
 * one the server then refuses.
 */

type Category = { key: string; label: string };
type Filed = { id: string; categoryLabel: string; summary: string };

export function SupportForm({
  initialText = "",
  path,
  learnerId,
  onFiled,
}: {
  /** Carried over from the chat, so switching does not lose what they typed. */
  initialText?: string;
  path?: string;
  learnerId?: string;
  onFiled?: (request: Filed) => void;
}) {
  const [category, setCategory] = useState("");
  const [text, setText] = useState(initialText);
  const [filed, setFiled] = useState<Filed | null>(null);

  const { data: catData } = useQuery(GET_SUPPORT_CATEGORIES);
  const categories: Category[] = (catData as any)?.supportCategories ?? [];
  const [fileIt, { loading }] = useMutation(FILE_SUPPORT_REQUEST);

  async function submit() {
    if (!category || !text.trim() || loading) return;
    const { data } = await fileIt({
      variables: {
        category,
        messages: [{ role: "user", text: text.trim() }],
        path,
        learnerId,
      },
    });
    const request = (data as any)?.fileSupportRequest?.request;
    if (request) {
      setFiled(request);
      onFiled?.(request);
    }
  }

  // The receipt, and the only one there is: nothing emails anybody back, so this
  // card is the whole of what a person gets. A persistent block rather than a
  // toast for exactly that reason.
  if (filed) {
    return (
      <div className="rounded-lg border border-green-300 bg-green-50 px-3.5 py-3 text-sm dark:border-green-800 dark:bg-green-950/40">
        <p className="flex items-center gap-1.5 font-medium text-green-900 dark:text-green-100">
          <CheckCircle2 className="h-4 w-4" />
          Užregistruota
        </p>
        <p className="mt-1 text-green-800 dark:text-green-200">
          {filed.categoryLabel} · Nr. {filed.id.slice(-6)}
        </p>
        <p className="mt-1.5 text-green-800/80 dark:text-green-200/80">
          Administratorius perskaitys tavo žinutę.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Select value={category} onValueChange={setCategory}>
        <SelectTrigger>
          <SelectValue placeholder="Apie ką tai?" />
        </SelectTrigger>
        <SelectContent>
          {categories.map((c) => (
            <SelectItem key={c.key} value={c.key}>
              {c.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Kas nutiko? Kuo daugiau smulkmenų, tuo lengviau surasti."
        rows={5}
      />

      <Button onClick={submit} disabled={!category || !text.trim() || loading} className="self-start">
        <Send className="mr-2 h-4 w-4" />
        {loading ? "Siunčiama..." : "Išsiųsti"}
      </Button>
    </div>
  );
}
